import { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { io } from 'socket.io-client';
import AuthContext from '../context/authContext';

const useSocketIO = () => {
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);
  const authCtx = useContext(AuthContext);

  useEffect(() => {
    if (!authCtx.token) {
      return;
    }

    const backendUrl = import.meta.env.VITE_BE_URL || 'http://localhost:3000';
    const socket = io(backendUrl, {
      transports: ['websocket'],
      auth: {
        token: authCtx.token,
      },
    });

    socket.on('connect', () => {
      setIsConnected(true);
    });

    socket.on('disconnect', reason => {
      setIsConnected(false);
      if (reason === 'io server disconnect') {
        // Server disconnected the socket, try to reconnect
        socket.connect();
      }
    });

    socket.on('connect_error', err => {
      console.error('Socket.io connection error:', err.message);
      setIsConnected(false);
    });

    socket.on('token_expired', () => {
      console.warn('Socket.io: token expired, disconnecting');
      setIsConnected(false);
      socket.disconnect();
      // Trigger logout to clear token and redirect to login
      if (authCtx.logout) {
        authCtx.logout();
      }
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
    };
  }, [authCtx.token]);

  const on = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.on(event, callback);
    }
  }, []);

  const off = useCallback((event, callback) => {
    if (socketRef.current) {
      socketRef.current.off(event, callback);
    }
  }, []);

  const emit = useCallback((event, payload) => {
    if (socketRef.current) {
      socketRef.current.emit(event, payload);
    }
  }, []);

  return { isConnected, on, off, emit };
};

export default useSocketIO;
